/**
 * PUBLIC licence verification. No authentication.
 *
 * This one screen serves two entry points:
 *   - `/verify-license` for a blank manual lookup page
 *   - `/license/verify/:licenseId` for QR/deep links that should run immediately
 *
 * The destination is public and outside the app shell because a third party needs to be able
 * to open it with nothing but a licence number.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ScanLine, Search, XCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Btn } from '../components/ui';
import { useLicense } from '../hooks';
import { c } from '../styles/theme';

/** One cell in the valid-result grid. */
function ResultCell({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        padding: '8px 10px',
        background: c.surface2,
      }}
    >
      {/* text2, not text3. The theme's own rule is that text3 is "NOT for text" — it
          measures 2.84:1 on this cell's surface2 background on dark and 2.40:1 on light,
          so it fails WCAG AA (4.5:1) on both, and at 10px uppercase it is the smallest
          text on the page. These labels are what name the value beside them: on a public
          page a third party opens to check a licence, "License number" and "Status" have
          to be legible for the value to mean anything. text2 clears AA on both themes
          (6.14:1 dark, 7.07:1 light); the size and letter-spacing still mark it as a
          label without fading it out. */}
      <p
        style={{
          fontSize: 10,
          color: c.text2,
          margin: '0 0 2px',
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}
      >
        {label}
      </p>
      <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{value}</p>
    </div>
  );
}

function statusLabel(status: string | undefined): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'revoked':
      return 'Revoked';
    case 'suspended':
      return 'Suspended';
    case 'refunded':
      return 'Refunded';
    case 'expired':
      return 'Expired';
    default:
      return 'Unknown';
  }
}

function ExactSongResult({
  matchedSong,
}: {
  matchedSong: { title: string; generatedAt: string; coverageReason: string; shareSlug?: string } | null;
}) {
  return (
    <div
      style={{
        border: `1px solid ${matchedSong ? c.success : c.danger}`,
        borderRadius: 10,
        background: c.surface1,
        padding: '14px 16px',
        marginTop: 14,
      }}
    >
      <p
        style={{
          margin: '0 0 6px',
          fontSize: 12.5,
          fontWeight: 600,
          color: c.text1,
        }}
      >
        {matchedSong ? 'This song is covered by this license' : 'No covered song matched that check'}
      </p>
      {matchedSong ? (
        <>
          <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2 }}>
            {matchedSong.title} · Generated {matchedSong.generatedAt}
          </p>
          <p style={{ margin: '0 0 4px', fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
            {matchedSong.coverageReason}
          </p>
          {matchedSong.shareSlug && (
            <p style={{ margin: 0, fontSize: 11, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
              Share slug: {matchedSong.shareSlug}
            </p>
          )}
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
          Check the song title, or provide the public share slug when one is available, to make
          the match more precise.
        </p>
      )}
    </div>
  );
}

export default function LicenseVerify() {
  const { licenseId } = useParams<{ licenseId: string }>();
  const { verifyQuery, setVerifyQuery, verification, verify } = useLicense();
  const [songTitle, setSongTitle] = useState('');
  const [songSlug, setSongSlug] = useState('');

  /**
   * When the check was run, shown as "Verified at".
   *
   * Stamped once per result rather than read from the clock during render — the
   * timestamp belongs to the lookup attempt, not to every re-render after it.
   */
  const [verifiedAt, setVerifiedAt] = useState('');

  // A licence id in the URL is a verification request: run it on arrival, and
  // again if the user navigates to a different id.
  //
  // `.then` without a `.catch` remains safe because `verify` does not reject — it records
  // a `'failed'` verification instead — and `finally` states that this stamp belongs to the
  // attempt rather than to a success, so a rejection introduced later cannot leave the
  // timestamp unset while a result is on screen.
  useEffect(() => {
    if (!licenseId) return;
    setVerifyQuery(licenseId);
    void verify({ licenseNumber: licenseId }).finally(() => setVerifiedAt(new Date().toLocaleString()));
    // `verify` and `setVerifyQuery` are stable store actions; re-running on their
    // identity would re-verify on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenseId]);

  async function handleVerify() {
    await verify({
      licenseNumber: verifyQuery,
      ...(songTitle.trim() ? { title: songTitle.trim() } : {}),
      ...(songSlug.trim() ? { slug: songSlug.trim() } : {}),
    });
    setVerifiedAt(new Date().toLocaleString());
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        boxSizing: 'border-box',
        padding: 'clamp(24px, 10vh, 96px) clamp(18px, 10vw, 160px)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 880, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ScanLine size={17} color={c.accent} />
          <h1
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 16,
              margin: 0,
              color: c.text1,
            }}
          >
            License Verification
          </h1>
        </div>
        <p style={{ fontSize: 12, color: c.text2, margin: '0 0 4px', lineHeight: 1.55 }}>
          Enter a licence number, or scan a certificate QR code, to confirm that a Lyrixsa AI
          licence is genuine. Both commercial (LYR-) and reseller (RSL-) licences verify here.
        </p>
        <p style={{ fontSize: 11, color: c.text2, margin: '0 0 18px', fontStyle: 'italic' }}>
          This page is public and requires no sign-in. Public verification shows only the licence
          details needed to confirm validity.
        </p>

        {/* A real <form>, so Enter in the query field runs the lookup — the same fix
            Login.tsx, NewProjectForm.tsx, Ticket.tsx and Playlists.tsx carry, with the same
            note: "a real <form> so Enter submits, which the prototype's div did not". A
            single search field is the strongest case for it — pressing Enter after typing a
            licence number is how every lookup on the web works, and this one did nothing.
            preventDefault because there is nowhere to post to; `handleVerify` is the
            submission and it already refuses an empty query. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerify();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label htmlFor="verify-query" className="lyx-visually-hidden">
              License number
            </label>
            <input
              id="verify-query"
              value={verifyQuery}
              onChange={(e) => setVerifyQuery(e.target.value)}
              placeholder="License number"
              style={{
                flex: '1 1 200px',
                background: c.surface1,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                fontSize: 13,
                padding: '9px 12px',
                color: c.text1,
              }}
            />
            <Btn type="submit" disabled={verification.status === 'checking'}>
              <Search size={13} />
              {verification.status === 'checking' ? 'Verifying…' : 'Verify'}
            </Btn>
          </div>

          <div
            style={{
              border: `1px solid ${c.border}`,
              borderRadius: 10,
              background: c.surface1,
              padding: '12px 14px',
            }}
          >
            <p style={{ margin: '0 0 8px', fontSize: 12, color: c.text1, fontWeight: 500 }}>
              Verify a specific song under this license
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={songTitle}
                onChange={(e) => setSongTitle(e.target.value)}
                placeholder="Song title"
                style={{
                  flex: '1 1 220px',
                  background: c.surface2,
                  border: `1px solid ${c.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                  padding: '8px 10px',
                  color: c.text1,
                }}
              />
              <input
                value={songSlug}
                onChange={(e) => setSongSlug(e.target.value)}
                placeholder="Optional share slug"
                style={{
                  flex: '1 1 180px',
                  background: c.surface2,
                  border: `1px solid ${c.border}`,
                  borderRadius: 8,
                  fontSize: 12,
                  padding: '8px 10px',
                  color: c.text1,
                  fontFamily: 'IBM Plex Mono',
                }}
              />
            </div>
            <p style={{ margin: '7px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.5 }}>
              Title is the normal check. A public share slug, when available, makes the match more
              precise.
            </p>
          </div>
        </form>

        {verification.status === 'valid' && (
          <div
            style={{
              background: c.surface1,
              border: `1px solid ${c.success}`,
              borderRadius: 12,
              padding: '18px 20px',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: c.success,
                background: c.successSoft,
                padding: '4px 10px',
                borderRadius: 999,
                width: 'fit-content',
                marginBottom: 14,
              }}
            >
              <CheckCircle2 size={12} />
              {verification.result.series === 'reseller' ? 'Reseller License' : 'License'}{' '}
              {statusLabel(verification.result.status)}
            </span>
            <div className="lyx-grid-2">
              <ResultCell label="License number" value={verification.result.licenseNumber ?? '—'} />
              <ResultCell label="Status" value={statusLabel(verification.result.status)} />
              <ResultCell label="Licensed to" value={verification.result.licensedTo ?? '—'} />
              <ResultCell label="Issued date" value={verification.result.issuedDate ?? '—'} />
              <ResultCell label="Product" value={verification.result.product ?? '—'} />
              <ResultCell
                label="Agreement version"
                value={verification.result.agreementVersion ? `v${verification.result.agreementVersion}` : '—'}
              />
              {/* Song coverage is a commercial-licence question. A reseller licence
                  covers the right to resell the app and no songs at all, so the cell is
                  omitted rather than showing 0 — which would read as "covers nothing"
                  about a licence that is fully valid for what it grants. */}
              {verification.result.songCoverageSummary && (
                <ResultCell
                  label="Covered songs"
                  value={String(verification.result.songCoverageSummary.coveredSongs ?? 0)}
                />
              )}
              <ResultCell label="Verified at" value={verifiedAt} />
            </div>

            {verification.result.songs && verification.result.songs.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: c.text1, fontWeight: 500 }}>
                  Covered songs under this license
                </p>
                <div
                  style={{
                    border: `1px solid ${c.border}`,
                    borderRadius: 10,
                    overflow: 'hidden',
                    background: c.surface2,
                  }}
                >
                  {verification.result.songs.map((song, index) => (
                    <div
                      key={`${song.title}-${song.generatedAt}-${index}`}
                      style={{
                        padding: '12px 14px',
                        borderBottom:
                          index === verification.result.songs!.length - 1 ? undefined : `1px solid ${c.border}`,
                      }}
                    >
                      <p style={{ margin: '0 0 4px', fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
                        {song.title}
                      </p>
                      <p style={{ margin: '0 0 4px', fontSize: 11, color: c.text2 }}>
                        Generated {song.generatedAt}
                      </p>
                      <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
                        {song.coverageReason}
                      </p>
                      {song.shareSlug && (
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
                          Share slug: {song.shareSlug}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* WHAT THIS LICENCE GRANTS. Shown for the reseller series because the
                grant is the whole answer there — there is no song list to imply it, and
                "valid" alone does not tell a third party what was authorised. */}
            {verification.result.series === 'reseller' &&
              verification.result.rightsSummary &&
              verification.result.rightsSummary.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: c.text1, fontWeight: 500 }}>
                    What this license grants
                  </p>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: 18,
                      fontSize: 11.5,
                      color: c.text2,
                      lineHeight: 1.7,
                    }}
                  >
                    {verification.result.rightsSummary.map((right) => (
                      <li key={right}>{right}</li>
                    ))}
                  </ul>
                  <p style={{ margin: '8px 0 0', fontSize: 11, color: c.text2, lineHeight: 1.5 }}>
                    A reseller license authorises resale of the app. It does not cover songs, and
                    it does not transfer ownership of Lyrixsa AI or its models.
                  </p>
                </div>
              )}

            {verification.result.matchedSong !== undefined && (
              <ExactSongResult matchedSong={verification.result.matchedSong} />
            )}
          </div>
        )}

        {verification.status === 'failed' && (
          /*
            THE CHECK COULD NOT BE RUN — deliberately NOT the "License Not Found" box below.
            That one is an answer about the licence; this one says nothing about the licence
            at all, only about the request. Reporting a network fault as "not found" would
            tell a valid licence holder, on the public page they hand to a client as proof,
            that their licence does not exist.

            Neutral colour rather than danger for the same reason, and the retry is the
            Verify button above, which is re-enabled because the status left `'checking'`.
          */
          <div
            role="alert"
            style={{
              background: c.surface1,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              padding: '18px 20px',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: c.text2,
                background: c.surface2,
                padding: '4px 10px',
                borderRadius: 999,
                marginBottom: 10,
              }}
            >
              <AlertTriangle size={12} />
              Could not check
            </span>
            <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
              {verification.message} This says nothing about whether the license is valid — the
              lookup itself did not complete.
            </p>
          </div>
        )}

        {verification.status === 'invalid' && (
          <div
            style={{
              background: c.surface1,
              border: `1px solid ${c.danger}`,
              borderRadius: 12,
              padding: '18px 20px',
              textAlign: 'center',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: c.danger,
                background: c.dangerSoft,
                padding: '4px 10px',
                borderRadius: 999,
                marginBottom: 10,
              }}
            >
              <XCircle size={12} />
              License Not Found
            </span>
            <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
              No Lyrixsa AI licence matches that number.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
